import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, Bot, Globe, Megaphone, LineChart, GraduationCap, Scale, ArrowUpRight, Sparkles } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Wordmark } from '@/components/Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollOrNav = (target: 'services' | 'contact' | 'process' | 'ai') => {
    if (location.pathname === '/') {
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${target}`;
    }
    setIsOpen(false);
  };

  const serviceItems = [
    { to: '/services/ai',       label: t('nav.ai'),       Icon: Bot,            accent: true,
      desc: language === 'pt' ? 'Chatbots, n8n, workflows' : 'Chatbots, n8n, workflows' },
    { to: '/services/web',      label: t('nav.web'),      Icon: Globe,
      desc: language === 'pt' ? 'Sites em 5 dias' : 'Sites in 5 days' },
    { to: '/services/social',   label: t('nav.social'),   Icon: Megaphone,
      desc: language === 'pt' ? 'Facebook · Instagram' : 'Facebook · Instagram' },
    { to: '/services/seo',      label: t('nav.seo'),      Icon: LineChart,
      desc: language === 'pt' ? 'SEO local + Ads' : 'Local SEO + Ads' },
    { to: '/services/training', label: t('nav.training'), Icon: GraduationCap,
      desc: language === 'pt' ? 'IA + Higiene Digital' : 'AI + Digital Hygiene' },
    { to: '/services/dpo',      label: t('nav.dpo'),      Icon: Scale,
      desc: language === 'pt' ? 'Conformidade RGPD' : 'GDPR compliance' },
  ];

  const isServiceRoute = location.pathname.startsWith('/services/');

  return (
    <nav
      className={[
        'sticky top-0 z-50 transition-all duration-500',
        scrolled
          ? 'bg-background/85 backdrop-blur-xl border-b border-border/60 shadow-card'
          : 'bg-transparent border-b border-transparent',
      ].join(' ')}
      aria-label="Primary"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Brand */}
          <Link to="/" aria-label="Spawncore — Home" className="-ml-1">
            <Wordmark size="md" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Services mega-dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={[
                    'group h-10 px-4 rounded-full text-sm font-medium transition-colors relative',
                    isServiceRoute
                      ? 'text-foreground bg-card/60'
                      : 'text-muted-foreground hover:text-foreground hover:bg-card/60',
                  ].join(' ')}
                >
                  {t('nav.services')}
                  <ChevronDown className="ml-1.5 h-3.5 w-3.5 opacity-60 transition-transform group-data-[state=open]:rotate-180" />
                  {isServiceRoute && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-lime" aria-hidden="true" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={12}
                className="w-80 bg-card border-border p-2"
              >
                {serviceItems.map(({ to, label, Icon, accent, desc }) => {
                  const active = location.pathname === to;
                  return (
                    <DropdownMenuItem key={to} asChild>
                      <Link
                        to={to}
                        className={[
                          'group flex items-center gap-3 rounded-lg p-3 cursor-pointer focus:bg-secondary transition-colors',
                          active ? 'bg-secondary/80' : '',
                        ].join(' ')}
                      >
                        <div
                          className={[
                            'flex h-10 w-10 items-center justify-center rounded-lg border transition-all',
                            accent
                              ? 'border-lime/30 bg-lime/[0.08] text-lime group-hover:bg-lime/[0.15] group-hover:border-lime/50'
                              : 'border-border bg-secondary text-foreground group-hover:border-foreground/30',
                          ].join(' ')}
                        >
                          <Icon className="h-4 w-4" strokeWidth={1.6} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                            {label}
                            {accent && (
                              <Sparkles className="h-3 w-3 text-lime" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {desc}
                          </div>
                        </div>
                        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => scrollOrNav('process')}
              className="h-10 px-4 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-card/60 smooth-transition"
            >
              {t('nav.work')}
            </button>

            <button
              onClick={() => scrollOrNav('contact')}
              className="h-10 px-4 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-card/60 smooth-transition"
            >
              {t('nav.contact')}
            </button>

            {/* Primary CTA */}
            <Button
              onClick={() => scrollOrNav('contact')}
              className="group ml-2 h-10 px-5 rounded-full bg-primary text-primary-foreground hover:bg-primary-light text-sm font-medium shadow-card border border-primary-light/20"
            >
              {t('nav.bookCall')}
              <ArrowUpRight className="ml-1.5 h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Button>

            {/* Language toggle — compact */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-1 h-10 px-3 rounded-full text-xs font-mono-tag uppercase tracking-[0.2em] gap-1.5 text-muted-foreground hover:text-foreground hover:bg-card/60"
                >
                  {language.toUpperCase()}
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={12} className="bg-card border-border">
                <DropdownMenuItem
                  onClick={() => setLanguage('pt')}
                  className={`cursor-pointer ${language === 'pt' ? 'bg-secondary text-lime' : ''}`}
                >
                  <span className="font-mono-tag text-xs uppercase tracking-[0.2em] mr-3">PT</span>
                  <span>Português</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('en')}
                  className={`cursor-pointer ${language === 'en' ? 'bg-secondary text-lime' : ''}`}
                >
                  <span className="font-mono-tag text-xs uppercase tracking-[0.2em] mr-3">EN</span>
                  <span>English</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
              className="text-foreground hover:bg-card/60 rounded-full"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="md:hidden py-6 border-t border-border/60 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="space-y-1 mb-6">
              <div className="eyebrow px-4 pb-3">— {t('nav.services')}</div>
              {serviceItems.map(({ to, label, Icon, accent, desc }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setIsOpen(false)}
                    className={[
                      'flex items-center gap-3 rounded-lg px-4 py-3 smooth-transition',
                      active ? 'bg-card' : 'hover:bg-card/60',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'flex h-10 w-10 items-center justify-center rounded-lg border',
                        accent ? 'border-lime/30 bg-lime/[0.08] text-lime' : 'border-border bg-secondary text-foreground',
                      ].join(' ')}
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.6} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground flex items-center gap-2">
                        {label}
                        {accent && <Sparkles className="h-3 w-3 text-lime" />}
                      </div>
                      <div className="text-xs text-muted-foreground">{desc}</div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="space-y-3 px-4 pt-4 border-t border-border/60">
              <button
                onClick={() => scrollOrNav('process')}
                className="block w-full text-left text-sm font-medium text-muted-foreground hover:text-foreground py-2"
              >
                {t('nav.work')}
              </button>
              <Button
                onClick={() => scrollOrNav('contact')}
                className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary-light font-medium"
              >
                {t('nav.bookCall')}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setLanguage('pt'); setIsOpen(false); }}
                  className={`rounded-full ${language === 'pt' ? 'border-lime/50 text-lime' : ''}`}
                >
                  <span className="font-mono-tag text-xs uppercase tracking-[0.2em] mr-2">PT</span>
                  Português
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setLanguage('en'); setIsOpen(false); }}
                  className={`rounded-full ${language === 'en' ? 'border-lime/50 text-lime' : ''}`}
                >
                  <span className="font-mono-tag text-xs uppercase tracking-[0.2em] mr-2">EN</span>
                  English
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
